/** @format */
import { GetServerSidePropsContext, NextPage } from 'next';
import { useTranslation } from 'next-i18next';
import Head from 'next/head';
import React, {
    Fragment,
    FunctionComponent,
    useContext,
    useEffect,
} from 'react';

import product from '@toucan/custom-server/services/product';

import { getServerSideProps as getSharedServerSideProps } from '@toucan/common/nextjs';

import { DataLayerEventType } from '@toucan/client/global/analytics';
import { centToDecimal } from '@toucan/client/global/currency';
import { MarketContext } from '@toucan/client/global/market';
import { ProductConditionValue } from '@toucan/client/global/product';

import {
    trackViaDataLayer,
    transformItemToDataLayerItem,
} from '@toucan/client/shared/helpers/analytics';
import { useRecentlyViewedProducts } from '@toucan/client/shared/hooks/use-recently-viewed-products';

import { Layout, LayoutType } from '@toucan/client/features/layout';
import { useProductData } from '@toucan/client/features/product/hooks/use-product-data';
import { MetaFactoryType, metaFactories } from '@toucan/client/features/seo';
import { getMetaElements } from '@toucan/client/features/seo/helpers/meta';

import { Product as ProductView } from '@toucan/client/views/product';

export type ProductPageProps = NonNullable<
    Awaited<ReturnType<typeof getSharedServerSideProps>>
>['props'] &
    Awaited<ReturnType<typeof product.getPageProps>>;

const defaultTestId = 'product-page';

const Product: FunctionComponent<ProductPageProps> = ({
    modelInfo,
    pageData,
    breadcrumbs,
    parentCategory,
    productInfo: initialProductInfo,
    stockInfo: initialStockInfo,
    modelRecommendations,
    otherStock,
}) => {
    const { activeMarket } = useContext(MarketContext);
    const { t } = useTranslation();
    const { productInfo, stockInfo, refreshProductData } = useProductData({
        initialProductInfo,
        initialStockInfo,
    });
    const { addRecentlyViewedProduct } = useRecentlyViewedProducts();

    const isNewProduct = productInfo.condition === ProductConditionValue.NEW;

    useEffect(() => {
        trackViaDataLayer({
            event: DataLayerEventType.PRODUCT_PAGE_VIEW,
            productId: isNewProduct ? null : productInfo.id,
            productSku: isNewProduct ? null : productInfo.sku,
            modelId: productInfo.modelId,
            modelName: productInfo.name,
            productValue: centToDecimal(productInfo.value),
            items: [
                transformItemToDataLayerItem({
                    name: productInfo.name,
                    id: productInfo.id,
                    currency: activeMarket.currency,
                    value: productInfo.value,
                    category: productInfo.primaryCategoryName,
                    condition: productInfo.condition,
                    brand: modelInfo.brand.name,
                }),
            ],
        });

        if (!isNewProduct) {
            addRecentlyViewedProduct(productInfo.id);
        }
    }, []);

    const metaElements = getMetaElements({
        factory: metaFactories[MetaFactoryType.PRODUCT](
            t,
            modelInfo.name,
            productInfo.condition === ProductConditionValue.NEW
        ),
        ignoreMount: true,
    });

    return (
        <Fragment>
            <Head>{metaElements}</Head>
            <ProductView
                data-testid={`${defaultTestId}__view`}
                productInfo={productInfo}
                pageData={pageData}
                stockInfo={stockInfo}
                breadcrumbs={breadcrumbs}
                parentCategory={parentCategory}
                modelInfo={modelInfo}
                modelRecommendations={modelRecommendations}
                otherStock={otherStock}
                refreshProductData={refreshProductData}
            />
        </Fragment>
    );
};

const ProductPage: NextPage<ProductPageProps> = (props) => {
    return (
        <Layout
            layoutType={LayoutType.FULL}
            data-testid={defaultTestId}
            {...props.layoutProps}
        >
            <Product {...props} />
        </Layout>
    );
};

const getServerSideProps = async (context: GetServerSidePropsContext) => {
    const { props: sharedProps } =
        (await getSharedServerSideProps(context)) ?? {};

    const { query } = context;

    const productPageProps = await product.getPageProps(query, context);

    return {
        props: {
            ...sharedProps,
            ...productPageProps,
        },
    };
};

export { getServerSideProps };
export default ProductPage;
